import {
  GetProduct,
  StrapiProductType,
  type GetProductQuery,
  type GetProductQueryVariables,
  type StrapiProduct,
} from '#variant/apollo/types/types';
import type { BreadCrumb } from '~/components/common/types';
import useStrapiSlugPageData from '~/composables/useStrapiSlugPageData';
import { useGoogleProduct } from '#variant/helpers/googleProduct';
import { escapeRegExp } from '~/helpers/fetchErrors';
import { useCurrencyStore } from '~/store/currency';

export default async (slugRef: Ref<string>) => {
  const { locale, t } = useI18n();
  const currencyStore = useCurrencyStore();
  const route = useRoute();

  type PageData = {
    googleProduct: string | null;
    strapiContent: StrapiProduct | null;
    strapiTopBar: GetProductQuery['product_top_bar'];
    strapiCountdown: GetProductQuery['product_countdown'];
    breadCrumbs: Array<BreadCrumb>;
  };

  const { data, pageContent, refresh, pending, error, isNotFound, isError } =
    await useStrapiSlugPageData<
      GetProductQuery,
      GetProductQueryVariables,
      PageData
    >({
      asyncDataKey: `gp-lp-${slugRef.value}-${locale.value}-${currencyStore.currency}`,
      slugRef,
      pageLabel: 'Landing page',
      query: GetProduct,
      variables: () => ({
        locale: locale.value,
        currency: currencyStore.currency,
        slugs: [`.*\\/${escapeRegExp(slugRef.value)}$`],
      }),
      getItems: (response) => response.products,
      defaultData: () => ({
        googleProduct: null,
        strapiContent: null,
        strapiTopBar: [],
        strapiCountdown: null,
        breadCrumbs: [],
      }),
      transform: (item, response) => {
        const lp = item as StrapiProduct;
        const breadCrumbs: Array<BreadCrumb> = [];

        if (
          lp.type !== StrapiProductType.Voiceserver &&
          lp.type !== StrapiProductType.Subscription &&
          !breadCrumbs.some((b) => b.link === 'gameserver/index')
        ) {
          breadCrumbs.push({
            title: t('general/menu/item/game_servers') as string,
            link: 'gameserver/index',
          });
        }

        if (!breadCrumbs.some((b) => b.link === slugRef.value)) {
          breadCrumbs.push({
            title: lp.title,
            link: slugRef.value,
          });
        }

        return {
          strapiContent: lp,
          strapiTopBar: response.product_top_bar,
          strapiCountdown: response.product_countdown,
          googleProduct: JSON.stringify(
            useGoogleProduct(route.fullPath, response),
          ),
          breadCrumbs,
        };
      },
    });

  return {
    data,
    landingPageContent: pageContent as ComputedRef<StrapiProduct | null>,
    refresh,
    pending,
    error,
    isNotFound,
    isError,
  };
};
